import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { constNull, flow, pipe } from "fp-ts/function";
import { eventRoute } from "../../../../../routes/org";
import { array, either, nonEmptyArray, option, task, taskEither } from "fp-ts";
import { errorsToMessage, runTask } from "../../../../../utils/fp";
import Image from "next/legacy/image";
import { Button, Container, Grid } from "@mui/material";
import {
    eventDetailCodec,
    eventDetailQuery,
    eventDetailQueryId,
} from "../../../../../hooks/event";
import { supabase } from "../../../../../utils/supabase/init";
import { executeSupabaseQuery } from "../../../../../utils/supabase/useQuery";
import { QueryError } from "../../../../../utils/supabase/QueryState";
import * as React from "react";
import { serializeMdx } from "../../../../../utils/mdx/serializeMdx";
import { TaskEither } from "fp-ts/TaskEither";
import { MdxContent } from "../../../../../domain/common";
import { Mdx } from "../../../../../utils/mdx/Mdx";
import { isRegistrationOpen } from "../../../../../domain/event";
import { eventAttendRoute, xathloUrl } from "../../../../../routes/xathlo";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ExternalLink } from "../../../../../components/common/Link";
import { useFirstRenderDate } from "../../../../../utils/useFirstRenderDate";
import { EventTitle } from "../../../../../components/org/EventTitle";
import { RaceList } from "../../../../../components/org/event/RaceList";
import { StartListBtn } from "../../../../../components/org/event/StartListBtn";
import CvkLayout from "../../../../../components/customOrgs/cvk/CvkLayout";

type SsrError =
    | { type: "query"; reason: string }
    | {
          type: "fetch";
          reason: QueryError;
      }
    | { type: "mdx"; reason: string };

export const getServerSideProps = async (context: GetServerSidePropsContext) =>
    pipe(
        { ...context.query, subdomain: "cvk" },
        eventRoute.queryCodec.decode,
        either.mapLeft(
            (e): SsrError => ({
                type: "query",
                reason: errorsToMessage(e),
            })
        ),
        taskEither.fromEither,
        taskEither.chain((query) =>
            pipe(
                executeSupabaseQuery(
                    eventDetailQueryId(query.eventId),
                    eventDetailCodec,
                    eventDetailQuery(query.eventId),
                    supabase
                ),
                taskEither.mapLeft(
                    (e): SsrError => ({
                        type: "fetch",
                        reason: e,
                    })
                ),
                taskEither.map((event) => ({
                    query,
                    event,
                }))
            )
        ),
        taskEither.chain((props) =>
            pipe(
                props.event.description,
                option.fromNullable,
                option.fold(
                    (): TaskEither<SsrError, MdxContent | null> =>
                        taskEither.right(null),
                    flow(
                        serializeMdx,
                        taskEither.mapLeft(
                            (reason): SsrError => ({
                                type: "mdx",
                                reason,
                            })
                        )
                    )
                ),
                taskEither.map((descriptionMdx) => ({
                    ...props,
                    descriptionMdx,
                }))
            )
        ),
        task.map(
            either.foldW(
                (e) => {
                    // TODO better error handling
                    return {
                        notFound: true as const,
                    };
                },
                (props) => ({
                    props,
                })
            )
        ),
        runTask
    );

export default (p: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const renderDate = useFirstRenderDate();

    return (
        <CvkLayout
            subdomain={p.query.subdomain}
            seo={{ title: `${p.event.title}` }} // TODO
        >
            <Container maxWidth={"md"}>
                <Grid container direction={"row-reverse"}>
                    <Grid item xs={12} md={5}>
                        {pipe(
                            p.event.banner_image,
                            option.fromNullable,
                            option.fold(constNull, (image) => (
                                <Image
                                    src={image.url}
                                    height={image.metadata.size.height}
                                    width={image.metadata.size.width}
                                />
                            ))
                        )}
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <EventTitle {...p.event} />
                        <ul>
                            {p.event.web == null ? null : (
                                <li>
                                    <ExternalLink href={p.event.web}>
                                        Web závodu
                                    </ExternalLink>
                                </li>
                            )}
                            {pipe(
                                p.event.files,
                                nonEmptyArray.fromArray,
                                option.fold(
                                    constNull,
                                    flow(
                                        array.map((file) => (
                                            <li key={file.url}>
                                                <ExternalLink href={file.url}>
                                                    {file.title}
                                                </ExternalLink>
                                            </li>
                                        ))
                                    )
                                )
                            )}
                        </ul>
                        {pipe(
                            isRegistrationOpen(renderDate, p.event),
                            option.fold(constNull, () => (
                                <Grid
                                    container
                                    spacing={1}
                                    alignItems={"center"}
                                    sx={{
                                        my: 2,
                                    }}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        textAlign={"center"}
                                    >
                                        <Button
                                            variant={"contained"}
                                            size={"large"}
                                            href={xathloUrl(
                                                eventAttendRoute.route({
                                                    eventId: p.event.id,
                                                })
                                            )}
                                            rel="noreferrer"
                                            target={"_blank"}
                                            endIcon={<OpenInNewIcon />}
                                        >
                                            Online Registrace
                                        </Button>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        textAlign={"center"}
                                    >
                                        <StartListBtn
                                            eventId={p.event.id}
                                            races={p.event.races}
                                        />
                                    </Grid>
                                </Grid>
                            ))
                        )}
                    </Grid>
                </Grid>

                {p.descriptionMdx == null ? null : (
                    <Mdx mdxContent={p.descriptionMdx} />
                )}
                {pipe(
                    p.event.races,
                    nonEmptyArray.fromArray,
                    option.fold(constNull, (races) => (
                        <RaceList
                            races={races}
                            resultsLinkProps={
                                p.event.results_published
                                    ? {
                                          eventId: p.event.id,
                                          subdomain: p.query.subdomain,
                                      }
                                    : undefined
                            }
                        />
                    ))
                )}
            </Container>
        </CvkLayout>
    );
};
